
import { defineComponent, reactive, ref } from 'vue';
import { useList } from '@/hooks/use-list';
import {
  getBasicInfo,
  getRedisKeyList,
  getRedisDetail,
  deleteKey,
  deleteKeys,
} from '@/api/monitor-center-kt';
import type {
  IBasicInfo,
  IKeysPageListItem,
  IKeysPageReq,
} from '@/api/monitor-center-kt/type.d';
import { ElMessage, ElMessageBox } from 'element-plus';
import AddKey from './components/add-key.vue';
import LogDialog from './components/log-dialog.vue';
import UpdateKey from './components/update-key.vue';
import UpdateValue from './components/update-value.vue';
import UpdateTtl from './components/update-ttl.vue';
import usePermissionConfig from './use-permission-config';
import MonacoEditor from '@/components/monaco-editor/index.vue';
import { Plus, Delete, Search } from '@element-plus/icons-vue';

export default defineComponent({
  components: {
    AddKey,
    LogDialog,
    UpdateKey,
    UpdateValue,
    UpdateTtl,
    MonacoEditor,
  },
  setup() {
    const {
      params,
      tableTotal,
      tableData,
      tableLoading,
      handleSearch,
      handleReset,
      handleSizeChange,
      handleCurrentChange,
    } = useList<IKeysPageListItem, IKeysPageReq>({
      request: {
        api: getRedisKeyList,
        params: {
          key: '',
          pageNum: 1,
          pageSize: 20,
        },
      },
      response: {
        handleResponseData(list) {
          list.forEach((item) => {
            item.value = '';
            item.ttl = '';
            item._loaded = false;
            item._isObject = false;
          });
          return list;
        },
      },
    });

    // function initRowValueType(row: IKeysPageListItem) {
    //   try {
    //     const res = JSON.parse(row.value);
    //     row._isObject = !!(res && typeof res === 'object');
    //   } catch (e) {
    //     row._isObject = false;
    //   }
    // }

    const addKeyVisible = ref(false);
    let currentOperateRow = {} as IKeysPageListItem;
    const logDialogConf = reactive({
      visible: false,
      key: '',
    });
    const updateKeyConf = reactive({
      visible: false,
      data: {},
    });
    const updateValueConf = reactive({
      visible: false,
      data: {},
    });
    const updateTtlConf = reactive({
      visible: false,
      data: {},
    });

    const refreshRowData = (data: IKeysPageListItem, updateKey = false) => {
      if (updateKey) {
        currentOperateRow.key = data.key;
      } else {
        currentOperateRow.value = data.value;
        currentOperateRow.ttl = data.ttl;
      }
      // initRowValueType(currentOperateRow);
    };

    const handleOperateSuccess = () => {
      handleSearch(params.value.pageNum as number);
    };

    const handleLogDialog = (row: IKeysPageListItem) => {
      logDialogConf.visible = true;
      logDialogConf.key = row.key;
    };
    const handleUpdateKey = (row: IKeysPageListItem) => {
      updateKeyConf.visible = true;
      updateKeyConf.data = { ...row };
      currentOperateRow = row;
    };
    const handleUpdateValue = (row: IKeysPageListItem) => {
      updateValueConf.visible = true;
      updateValueConf.data = { ...row };
      currentOperateRow = row;
    };
    const handleUpdateTtl = (row: IKeysPageListItem) => {
      updateTtlConf.visible = true;
      updateTtlConf.data = { ...row };
      currentOperateRow = row;
    };

    let selectItemKeys: string[] = [];
    const delBtnDisable = ref(true);
    const handleSelectionChange = (lists: IKeysPageListItem[]) => {
      selectItemKeys = lists.map(item => item.key);
      delBtnDisable.value = !selectItemKeys.length;
    };
    const handleDeletekey = async (row: IKeysPageListItem) => {
      ElMessageBox.confirm('是否删除，此操作不可撤销',
        '信息', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }).then(async () => {
        await deleteKey(row.key);
        ElMessage.success('操作成功！');
        handleOperateSuccess();
      }).catch(() => {
      });
    };
    const handleDeletekeys = async () => {
      ElMessageBox.confirm('是否删除选中记录，此操作不可撤销',
        '信息', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }).then(async () => {
        await deleteKeys(selectItemKeys);
        ElMessage.success('操作成功！');
        selectItemKeys = [];
        handleOperateSuccess();
      }).catch(() => {
      });
    };

    const formatTime = (timestamp: number) => {
      const days = Math.floor(timestamp / (24 * 3600));
      const hours = Math.floor((timestamp % (24 * 60 * 60)) / (3600));
      return `${days}天${hours}小时`;
    };

    const basicInfo = ref<IBasicInfo>({
      isGtMax: false,
      keys_count: 0,
      keyspace_hits: '',
      uptime_in_seconds: '',
      used_memory_human: '',
      used_memory_peak_human: '',
    });
    const handleGetBasicInfo = async () => {
      const { data } = await getBasicInfo();
      basicInfo.value = {
        ...data,
        uptime_in_seconds: formatTime(+data.uptime_in_seconds),
      };
    };

    const handleGetRedisDetail = async (row: IKeysPageListItem) => {
      const { data } = await getRedisDetail(row.key);
      row.value = data.value;
      row.ttl = data.ttl;

      // initRowValueType(row);

      row._loaded = true;
    };

    const detailDialogInfo = ref({
      visible: false,
      value: '',
      title: '',
    });
    const handleShowDetail = (value: string) => {
      detailDialogInfo.value.visible = true;
      detailDialogInfo.value.value = value;
    };

    const init = () => {
      handleSearch();
      handleGetBasicInfo();
    };

    init();

    const permissionConfig = usePermissionConfig();

    return {
      params,
      tableTotal,
      tableData,
      tableLoading,
      handleSearch,
      handleReset,
      handleSizeChange,
      handleCurrentChange,
      addKeyVisible,
      logDialogConf,
      updateKeyConf,
      updateValueConf,
      updateTtlConf,
      basicInfo,
      handleGetBasicInfo,
      handleGetRedisDetail,
      refreshRowData,
      handleOperateSuccess,
      handleLogDialog,
      handleUpdateKey,
      handleUpdateValue,
      handleUpdateTtl,
      delBtnDisable,
      handleSelectionChange,
      handleDeletekey,
      handleDeletekeys,
      detailDialogInfo,
      handleShowDetail,
      permissionConfig,
      Plus,
      Delete,
      Search,
    };
  },
});
